'use client'

import { useMemo } from 'react'
import { Quote, ShieldAlert, MessageCircle, Trophy } from 'lucide-react'
import type { SavedRun } from '@/types/radar'
import {
  hasCitationEvidence,
  readProviderCitation,
} from '@/lib/radar/answer-engine-result'

// ── Types ────────────────────────────────────────────────────────────────────

interface ProviderSnapshot {
  provider: string
  snippet: string
  sentiment: string
  cited: boolean
  mentionRate: number
  competitorCount: number
  tone?: string
  inaccuracies?: number
  responseExcerpt?: string
}

interface DiffEntry {
  provider: string
  before: ProviderSnapshot | null
  after: ProviderSnapshot | null
}

// ── Extractors ───────────────────────────────────────────────────────────────

function extractCitationSnapshots(
  toolData: Record<string, unknown>
): ProviderSnapshot[] {
  const citation = toolData?.citation as {
    providers?: {
      provider: string
      available: boolean
      overallMentionRate: number
      queries?: {
        snippet: string
        sentiment: string
        brandMentioned: boolean
        urlCited: boolean
        sentimentDetail?: { tone: string }
      }[]
    }[]
    narrativeSummaries?: {
      provider: string
      summary: string
      tone: string
    }[]
  } | null

  if (!citation?.providers) return []

  return citation.providers
    .filter(p => p.available)
    .map(p => {
      const bestQuery = p.queries?.find(q => q.brandMentioned) || p.queries?.[0]
      const narrative = citation.narrativeSummaries?.find(
        n => n.provider.toLowerCase() === p.provider.toLowerCase()
      )
      return {
        provider: p.provider,
        snippet: narrative?.summary || bestQuery?.snippet || '',
        sentiment: bestQuery?.sentiment || 'not_mentioned',
        cited: p.overallMentionRate > 0,
        mentionRate: p.overallMentionRate,
        competitorCount: 0,
        tone: narrative?.tone || bestQuery?.sentimentDetail?.tone || '',
      }
    })
}

function extractAnswerSnapshots(
  toolData: Record<string, unknown>
): ProviderSnapshot[] {
  const answer = toolData?.answer as {
    providers?: {
      provider: string
      available: boolean
      snippet: string
    }[]
  } | null

  if (!answer?.providers) return []

  // Current and historical provider records both read through the shared
  // citation reader, so a legacy record renders without re-creating the old
  // mixed semantics. `competitorCount` counts VERIFIED competitors only.
  return answer.providers
    .filter(p => p.available)
    .map(p => {
      const citation = readProviderCitation(p)
      const evidenced = hasCitationEvidence(citation)
      return {
        provider: p.provider,
        snippet: p.snippet || '',
        sentiment: evidenced ? 'positive' : 'not_mentioned',
        cited: evidenced,
        mentionRate: evidenced ? 100 : 0,
        competitorCount: citation.verifiedCompetitorUrls.length,
      }
    })
}

function extractHallucinationSnapshots(
  toolData: Record<string, unknown>
): ProviderSnapshot[] {
  const hallucination = toolData?.hallucinationCheck as {
    scoreStatus?: 'graded' | 'withheld'
    overallScore?: number | null
    providerDetails?: {
      provider: string
      responseExcerpt: string
      flags: { severity: string }[]
      evaluationStatus?: 'judged' | 'indeterminate'
    }[]
    totalInaccuracies?: number
  } | null

  if (
    !hallucination?.providerDetails ||
    hallucination.scoreStatus === 'withheld' ||
    hallucination.overallScore === null
  )
    return []

  return hallucination.providerDetails
    .filter(p => p.evaluationStatus !== 'indeterminate')
    .map(p => ({
      provider: p.provider,
      snippet: '',
      sentiment: p.flags.length === 0 ? 'positive' : 'negative',
      cited: true,
      mentionRate: 100,
      competitorCount: 0,
      inaccuracies: p.flags.length,
      responseExcerpt: p.responseExcerpt || '',
    }))
}

function buildDiffs(
  beforeSnapshots: ProviderSnapshot[],
  afterSnapshots: ProviderSnapshot[]
): DiffEntry[] {
  const allProviders = new Set([
    ...beforeSnapshots.map(s => s.provider),
    ...afterSnapshots.map(s => s.provider),
  ])

  return Array.from(allProviders).map(provider => ({
    provider,
    before: beforeSnapshots.find(s => s.provider === provider) || null,
    after: afterSnapshots.find(s => s.provider === provider) || null,
  }))
}

// ── Sub-components ───────────────────────────────────────────────────────────

// ── Main Component ───────────────────────────────────────────────────────────

export function LLMAnswerDiff({
  currentRun,
  previousRun,
  onMarkAsWin,
}: {
  currentRun: {
    toolData: Record<string, unknown>
    timestamp: number
    unifiedScore: number
    domain?: string
  }
  previousRun: SavedRun
  /** Called when user clicks "Mark as Win" on a citation flip */
  onMarkAsWin?: (wins: { provider: string; description: string }[]) => void
}) {
  const sections = useMemo(() => {
    const prevData = previousRun.toolData as Record<string, unknown>
    const currData = currentRun.toolData

    const result: {
      title: string
      icon: typeof Quote
      diffs: DiffEntry[]
      type: 'citation' | 'answer' | 'hallucination'
    }[] = []

    // Citation diffs
    const citBefore = extractCitationSnapshots(prevData)
    const citAfter = extractCitationSnapshots(currData)
    if (citBefore.length > 0 || citAfter.length > 0) {
      result.push({
        title: 'How AI describes your brand',
        icon: Quote,
        diffs: buildDiffs(citBefore, citAfter),
        type: 'citation',
      })
    }

    // Answer engine diffs
    const ansBefore = extractAnswerSnapshots(prevData)
    const ansAfter = extractAnswerSnapshots(currData)
    if (ansBefore.length > 0 || ansAfter.length > 0) {
      result.push({
        title: 'Citation status by provider',
        icon: MessageCircle,
        diffs: buildDiffs(ansBefore, ansAfter),
        type: 'answer',
      })
    }

    // Hallucination diffs
    const halBefore = extractHallucinationSnapshots(prevData)
    const halAfter = extractHallucinationSnapshots(currData)
    if (halBefore.length > 0 || halAfter.length > 0) {
      result.push({
        title: 'Factual accuracy by provider',
        icon: ShieldAlert,
        diffs: buildDiffs(halBefore, halAfter),
        type: 'hallucination',
      })
    }

    return result
  }, [currentRun.toolData, previousRun.toolData])

  // Empty state CTA
  if (sections.length === 0) {
    return (
      <div className='rounded-lg border border-border/50 bg-muted/20 px-4 py-5 text-center'>
        <span className='block text-xs text-muted-foreground mb-2'>
          Run Citation, Answer Engine, or Hallucination Check in both scans to
          see how AI answers change.
        </span>
      </div>
    )
  }

  const beforeDate = new Date(previousRun.timestamp)
  const afterDate = new Date(currentRun.timestamp)
  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  // Summary stats
  const totalChanges = sections.reduce((sum, s) => {
    return (
      sum +
      s.diffs.filter(d => {
        if (!d.before || !d.after) return true
        return (
          d.before.sentiment !== d.after.sentiment ||
          d.before.cited !== d.after.cited ||
          d.before.snippet !== d.after.snippet
        )
      }).length
    )
  }, 0)

  // Citation flips (Not cited -> Cited = win)
  const citationWins: { provider: string }[] = []
  const citationLosses: { provider: string }[] = []
  sections.forEach(s => {
    s.diffs.forEach(d => {
      if (d.before && d.after && d.before.cited !== d.after.cited) {
        if (d.after.cited) citationWins.push({ provider: d.provider })
        else citationLosses.push({ provider: d.provider })
      }
    })
  })

  const sentimentShifts = sections.reduce((sum, s) => {
    return (
      sum +
      s.diffs.filter(
        d => d.before && d.after && d.before.sentiment !== d.after.sentiment
      ).length
    )
  }, 0)

  // Competitor displacement
  const competitorDrops = sections.reduce((sum, s) => {
    return (
      sum +
      s.diffs.reduce((dSum, d) => {
        if (!d.before || !d.after) return dSum
        const drop = d.before.competitorCount - d.after.competitorCount
        return dSum + Math.max(0, drop)
      }, 0)
    )
  }, 0)

  return (
    <div className='space-y-3'>
      {/* Header */}
      <div className='flex items-baseline justify-between'>
        <div className='flex items-baseline gap-2'>
          <h3 className='text-xs font-bold text-foreground leading-none'>
            LLM Answer Diff
          </h3>
          <span className='text-2xs text-muted-foreground leading-none'>
            {formatDate(beforeDate)} vs {formatDate(afterDate)}
          </span>
        </div>
      </div>

      {/* Citation win celebration */}
      {citationWins.length > 0 && (
        <div
          className='rounded-lg px-4 py-3 flex items-start gap-3'
          style={{
            background: 'linear-gradient(135deg, #22c55e08, #008dcc08)',
            border: '1px solid #22c55e25',
          }}
        >
          <div
            className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
            style={{ background: '#22c55e15' }}
          >
            <Trophy size={16} style={{ color: '#22c55e' }} />
          </div>
          <div>
            <span className='block text-sm font-bold text-foreground'>
              You&apos;re now cited by{' '}
              {citationWins.map(w => w.provider).join(', ')}
            </span>
            <span className='block text-xs text-muted-foreground mt-0.5'>
              {citationWins.length === 1
                ? 'This provider is now referencing your brand in AI-generated answers. This is the outcome your fixes were designed to achieve.'
                : `${citationWins.length} providers are now referencing your brand. Your action items are working.`}
            </span>
            {onMarkAsWin && (
              <button
                onClick={() =>
                  onMarkAsWin(
                    citationWins.map(w => ({
                      provider: w.provider,
                      description: `${w.provider}: Not cited -> Cited`,
                    }))
                  )
                }
                className='inline-flex items-center gap-1.5 mt-2 text-[11px] font-bold px-3 py-1.5 rounded-md transition-all hover:brightness-110'
                style={{ background: '#22c55e', color: '#fff' }}
              >
                <Trophy size={11} />
                Mark as Win
              </button>
            )}
          </div>
        </div>
      )}

      {/* Summary strip */}
      <div className='flex items-center gap-4 px-3 py-2 rounded-lg bg-muted/40'>
        <span className='text-[11px] text-muted-foreground'>
          <span className='font-bold text-foreground'>{totalChanges}</span>{' '}
          changes detected
        </span>
        {citationWins.length > 0 && (
          <span className='text-[11px]'>
            <span className='font-bold' style={{ color: '#22c55e' }}>
              {citationWins.length}
            </span>{' '}
            <span className='text-muted-foreground'>
              citation win{citationWins.length !== 1 ? 's' : ''}
            </span>
          </span>
        )}
        {citationLosses.length > 0 && (
          <span className='text-[11px]'>
            <span className='font-bold' style={{ color: '#F90B8A' }}>
              {citationLosses.length}
            </span>{' '}
            <span className='text-muted-foreground'>
              citation loss{citationLosses.length !== 1 ? 'es' : ''}
            </span>
          </span>
        )}
        {sentimentShifts > 0 && (
          <span className='text-[11px]'>
            <span className='font-bold' style={{ color: '#008dcc' }}>
              {sentimentShifts}
            </span>{' '}
            <span className='text-muted-foreground'>
              tone shift{sentimentShifts !== 1 ? 's' : ''}
            </span>
          </span>
        )}
        {competitorDrops > 0 && (
          <span className='text-[11px]'>
            <span className='font-bold' style={{ color: '#22c55e' }}>
              {competitorDrops}
            </span>{' '}
            <span className='text-muted-foreground'>
              competitor{competitorDrops !== 1 ? 's' : ''} displaced
            </span>
          </span>
        )}
      </div>

      {/* The blurred section preview and its "Upgrade to Pro Retainer"
          overlay were removed 2026-07-26. That gate had NO tier condition —
          it blurred for free and Pro Retainer customers alike, so it never
          gated a view anyone could buy; it was a permanent advert sitting
          inside a paid product.

          The per-provider detail behind it is real, computed data. Exposing
          it is a pricing decision, not a layout one, so it is omitted here
          rather than silently unblurred. The measured summary above — the
          citation flips, tone shifts and displacements — is what this panel
          now reports. */}
    </div>
  )
}
